import React, { useEffect, useRef, useState } from "react";
import {
  BatteryFull,
  Bell,
  LayoutGrid,
  LogOut,
  Pin,
  Power,
  Settings as SettingsIcon,
  X,
  Moon,
  Search,
  Sun,
  Volume2,
  Wifi,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useWindows } from "../WindowManager";
import { useViewMode } from "../../ViewMode";
import { useMenu } from "./ContextMenu";
import { THIS_PC } from "../fs";
import "./Taskbar.css";

export function WindowsLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <rect x="0" y="0" width="9" height="9" rx="1.2" fill="currentColor" />
      <rect x="11" y="0" width="9" height="9" rx="1.2" fill="currentColor" />
      <rect x="0" y="11" width="9" height="9" rx="1.2" fill="currentColor" />
      <rect x="11" y="11" width="9" height="9" rx="1.2" fill="currentColor" />
    </svg>
  );
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 20);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function Taskbar({ mobile, startOpen, onToggleStart }) {
  const {
    apps,
    windows,
    activeId,
    toggleApp,
    open,
    showDesktop,
    close: closeWindow,
  } = useWindows();
  const { isDark, toggleTheme } = useTheme();
  const { exitWindows } = useViewMode();
  const { showMenu } = useMenu();
  const now = useClock();
  const [quickOpen, setQuickOpen] = useState(false);
  const quickRef = useRef(null);

  useEffect(() => {
    if (!quickOpen) return;
    const onDown = (e) => {
      if (!quickRef.current?.contains(e.target)) setQuickOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [quickOpen]);

  const openIds = new Set(windows.map((w) => w.appId));
  const activeAppId = windows.find((w) => w.id === activeId)?.appId;
  const taskbarApps = mobile
    ? apps.filter((a) => openIds.has(a.id))
    : apps.filter((a) => a.pinned || openIds.has(a.id));

  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const date = now.toLocaleDateString([], {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });

  return (
    <div className="taskbar" role="toolbar" aria-label="Taskbar">
      <div className="taskbar__center">
        <button
          className={`tb-btn tb-btn--start ${startOpen ? "tb-btn--on" : ""}`}
          onContextMenu={(e) =>
            showMenu(e, [
              { label: "Apps and Features", icon: LayoutGrid, onClick: () => open("settings") },
              { label: "Settings", icon: SettingsIcon, onClick: () => open("settings") },
              { label: "File Explorer", icon: Search, onClick: () => open("explorer", { folderId: THIS_PC.id }) },
              { label: "Task Manager", icon: LayoutGrid, onClick: () => open("taskmgr") },
              { label: "Terminal", icon: Search, onClick: () => open("terminal") },
              { type: "separator" },
              { label: "Desktop", icon: LayoutGrid, onClick: showDesktop },
              { label: "Sign out to the classic site", icon: Power, onClick: exitWindows },
            ])
          }
          onClick={onToggleStart}
          aria-label="Start"
          aria-expanded={startOpen}
          title="Start"
        >
          <span className="tb-btn__logo">
            <WindowsLogo />
          </span>
        </button>

        {!mobile && (
          <button
            className="tb-search"
            onClick={onToggleStart}
            aria-label="Search"
          >
            <Search size={15} />
            <span>Search</span>
          </button>
        )}

        {taskbarApps.map((app) => {
          const isOpen = openIds.has(app.id);
          return (
            <button
              key={app.id}
              data-taskbar-app={app.id}
              className={`tb-btn ${isOpen ? "tb-btn--open" : ""} ${
                activeAppId === app.id ? "tb-btn--active" : ""
              }`}
              onClick={() => (isOpen ? toggleApp(app.id) : open(app.id))}
              onContextMenu={(e) =>
                showMenu(e, [
                  { label: app.title, icon: app.icon, onClick: () => open(app.id) },
                  { type: "separator" },
                  {
                    label: app.pinned ? "Unpin from taskbar" : "Pin to taskbar",
                    icon: Pin,
                    disabled: true,
                  },
                  {
                    label: "Close window",
                    icon: X,
                    disabled: !isOpen,
                    onClick: () => {
                      const win = windows.find((w) => w.appId === app.id);
                      if (win) closeWindow(win.id);
                    },
                  },
                ])
              }
              aria-label={app.title}
              title={app.title}
            >
              <span className="tb-btn__icon" style={{ "--tile": app.color }}>
                <app.icon size={17} strokeWidth={1.9} />
              </span>
              <span className="tb-btn__indicator" />
            </button>
          );
        })}
      </div>

      <div className="taskbar__tray">
        <button
          className="tray-exit"
          onClick={exitWindows}
          title="Leave the desktop and go back to the classic site"
        >
          <LogOut size={14} />
          <span>Classic site</span>
        </button>

        <button
          className="tray-btn"
          onClick={toggleTheme}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Light mode" : "Dark mode"}
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <div className="tray-quick" ref={quickRef}>
          <button
            className={`tray-btn tray-btn--group ${
              quickOpen ? "tray-btn--on" : ""
            }`}
            onClick={() => setQuickOpen((v) => !v)}
            aria-label="Quick settings"
            aria-expanded={quickOpen}
          >
            <Wifi size={15} />
            <Volume2 size={15} />
            <BatteryFull size={15} />
          </button>

          {quickOpen && (
            <div className="quick-panel" role="dialog" aria-label="Quick settings">
              <div className="quick-panel__grid">
                <button className="quick-tile quick-tile--on" type="button">
                  <Wifi size={17} />
                  <span>Wi-Fi</span>
                </button>
                <button
                  className={`quick-tile ${isDark ? "quick-tile--on" : ""}`}
                  onClick={toggleTheme}
                  type="button"
                >
                  <Moon size={17} />
                  <span>Dark mode</span>
                </button>
              </div>
              <label className="quick-slider">
                <Volume2 size={15} />
                <input
                  type="range"
                  min="0"
                  max="100"
                  defaultValue="72"
                  aria-label="Volume"
                />
              </label>
            </div>
          )}
        </div>

        <button className="tray-clock" onClick={() => {}} aria-label="Date and time">
          <span>{time}</span>
          <span>{date}</span>
        </button>

        <button className="tray-btn tray-btn--bell" aria-label="Notifications">
          <Bell size={15} />
        </button>

        {!mobile && (
          <button
            className="tray-peek"
            onClick={showDesktop}
            aria-label="Show desktop"
            title="Show desktop"
          />
        )}
      </div>
    </div>
  );
}
